/* Public, user-facing search result card.
 *
 * The menu item is the hero (it's what the user searched), with its description
 * as the prominent body copy and the restaurant a quiet secondary line. Kept
 * deliberately separate from the directory/admin card (SpotCard / .fc-card),
 * which has its own requirements. Address + "Visit site" only render when the
 * hit carries them — the search API does not send those fields yet. */
import React, { useState } from "react";
import type { MenuSearchHit } from "../../api/search";
import { PLACEHOLDER } from "../../api/restaurants";
import type { VenueType } from "../../data/adminData";

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const MONOGRAM_STOPWORDS = new Set([
  "the", "a", "an", "and", "of", "on", "at", "in", "el", "la", "los", "las",
]);

/* Two-letter monogram for the restaurant chip ("Curryosity on Juniper" → "CJ"). */
function monogram(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  const significant = words.filter(
    (w) => !MONOGRAM_STOPWORDS.has(w.toLowerCase()),
  );
  return (significant.length ? significant : words)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

/* Drop empty values and the API's "Category" stand-in. */
function real(value: string): boolean {
  return Boolean(value) && value !== PLACEHOLDER;
}

function UtensilsIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3v7a2 2 0 0 0 4 0V3M7 10v11M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10" />
    </svg>
  );
}

function TentIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h18M12 4 3 20M12 4l9 16M12 9l-5 11M12 9l5 11" />
    </svg>
  );
}

function HomeIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11 12 4l8 7M6 10v9h12v-9" />
    </svg>
  );
}

function PinIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11Z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  );
}

function ArrowIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  );
}

function CopyIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

function CheckIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 12 10 18 20 6" />
    </svg>
  );
}

const VENUE_META: Record<VenueType, { color: string; Icon: () => React.ReactElement }> = {
  "Restaurant":     { color: "#0E7C6F", Icon: UtensilsIcon },
  "Farmers Market": { color: "#A12A4A", Icon: TentIcon },
  "Micro Kitchen":  { color: "#C77A12", Icon: HomeIcon },
};

interface SearchResultCardProps {
  hit: MenuSearchHit;
  index: number;
}

export function SearchResultCard({
  hit,
  index,
}: SearchResultCardProps): React.ReactElement {
  const v = VENUE_META[hit.venue] ?? VENUE_META["Restaurant"];
  const VIcon = v.Icon;
  const [copied, setCopied] = useState(false);

  function onCopy() {
    if (!hit.address) return;
    copyText(hit.address).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    });
  }

  return (
    <article className="mcard" style={{ "--i": String(index) } as React.CSSProperties}>
      <div className="mcard-top">
        <span
          className="mcard-venue"
          style={{ color: v.color, background: v.color + "18" }}
        >
          <VIcon /> {hit.venue}
        </span>
        {real(hit.section) && (
          <span className="mcard-section">{hit.section}</span>
        )}
      </div>

      <h3 className="mcard-name">{hit.name}</h3>

      {hit.desc && <p className="mcard-desc">{hit.desc}</p>}

      <p className="mcard-where">
        <span className="avi" aria-hidden="true">
          {monogram(hit.restaurant)}
        </span>
        <b>{hit.restaurant}</b>
      </p>

      {hit.address && (
        <div className="mcard-addr">
          <span className="pin"><PinIcon /></span>
          <span className="addr-text">{hit.address}</span>
          <button
            className={"mcard-copy" + (copied ? " copied" : "")}
            onClick={onCopy}
            aria-label="Copy address"
          >
            {copied ? <><CheckIcon /> Copied</> : <><CopyIcon /> Copy</>}
          </button>
        </div>
      )}

      <div className="mcard-foot">
        <span className="price">{hit.price}</span>
        {hit.url && (
          <a
            className="mcard-visit"
            href={hit.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit site <ArrowIcon />
          </a>
        )}
      </div>
    </article>
  );
}
